// src/components/workflows/WorkflowDashboardView.jsx - Dashboard view for client automation workflows
import React, { useEffect, useState } from 'react';
import useWorkflowStore from '../../stores/WorkflowStore';
import { StatusCard, ScheduleControl, ResultsSummary, LogsTable, ManualTriggerButton } from './views';
import { Separator } from '../ui/separator.tsx';

/**
 * WorkflowDashboardView - Renders automation dashboard based on view_config
 *
 * view_config structure:
 * {
 *   header: {
 *     title: "Invoice Scraper",
 *     actions: ["manualTrigger"]
 *   },
 *   layout: [
 *     { row: 1, components: ["statusCard"], cols: "full" },
 *     { row: 2, components: ["scheduleControl", "resultsSummary"], cols: "half" },
 *     { row: 3, components: ["logsTable"], cols: "full" }
 *   ]
 * }
 */
const WorkflowDashboardView = ({ workflow }) => {
  const {
    automationRuns,
    loadAutomationRuns,
    loadAutomationLogs,
    triggerAutomation,
    updateAutomationSchedule,
  } = useWorkflowStore();

  const [runsWithLogs, setRunsWithLogs] = useState({});

  // Load runs on mount
  useEffect(() => {
    if (workflow?.id) {
      loadAutomationRuns(workflow.id);
    }
  }, [workflow?.id, loadAutomationRuns]);

  // Get data from workflow
  const automationConfig = workflow?.automation_config || {};
  const viewConfig = automationConfig.view_config || getDefaultViewConfig();
  const schedule = automationConfig.schedule || {};
  const runs = automationRuns[workflow?.id] || [];
  const lastRun = runs[0] || null;

  // Calculate next run time (simplified)
  const getNextRunTime = () => {
    if (!schedule.enabled) return null;
    // This would need proper calculation based on schedule type
    // For now, return a placeholder
    return null;
  };

  // Handle schedule update
  const handleScheduleUpdate = (updates) => {
    if (workflow?.id) {
      updateAutomationSchedule(workflow.id, updates);
    }
  };

  // Handle manual trigger
  const handleTrigger = async () => {
    if (workflow?.id) {
      await triggerAutomation(workflow.id);
    }
  };

  // Handle load logs for a run
  const handleLoadLogs = async (runId) => {
    if (!runsWithLogs[runId] && workflow?.id) {
      const logs = await loadAutomationLogs(workflow.id, runId);
      setRunsWithLogs(prev => ({ ...prev, [runId]: logs || [] }));
    }
  };

  // Merge logs into runs
  const runsWithLogsMerged = runs.map(run => ({
    ...run,
    logs: runsWithLogs[run.id] || run.logs || [],
  }));

  // Component registry
  const componentRegistry = {
    statusCard: () => (
      <StatusCard
        lastRun={lastRun}
        nextRun={getNextRunTime()}
        isScheduleEnabled={schedule.enabled}
      />
    ),
    scheduleControl: () => (
      <ScheduleControl
        schedule={schedule}
        onUpdate={handleScheduleUpdate}
      />
    ),
    resultsSummary: () => (
      <ResultsSummary
        summary={lastRun?.result_summary || {}}
        lastRunTime={lastRun?.completed_at}
      />
    ),
    logsTable: () => (
      <LogsTable
        runs={runsWithLogsMerged}
        onLoadLogs={handleLoadLogs}
      />
    ),
    manualTrigger: () => (
      <ManualTriggerButton
        onTrigger={handleTrigger}
        isRunning={lastRun?.status === 'running'}
      />
    ),
  };

  // Render a component by name
  const renderComponent = (name) => {
    const Component = componentRegistry[name];
    return Component ? <Component key={name} /> : null;
  };

  // Get grid classes based on cols config
  const getGridClasses = (cols, componentCount) => {
    if (cols === 'full' || componentCount === 1) {
      return 'grid grid-cols-1 gap-8';
    }
    if (cols === 'half' || componentCount === 2) {
      return 'grid grid-cols-1 md:grid-cols-2 gap-8';
    }
    if (cols === 'third' || componentCount === 3) {
      return 'grid grid-cols-1 md:grid-cols-3 gap-8';
    }
    return 'grid grid-cols-1 gap-8';
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
      <div className="flex-1 overflow-y-auto pt-12 pl-24 pr-24 pb-12">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-foreground">
              {viewConfig.header?.title || workflow?.name || 'Automation'}
            </h1>
            {workflow?.description && (
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-2xl">
                {workflow.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {viewConfig.header?.actions?.map(action => renderComponent(action))}
          </div>
        </div>

        <Separator className="mb-10" />

        {/* Layout Rows */}
        <div className="space-y-10">
          {viewConfig.layout?.map((row, index) => (
            <div key={row.row || index} className={getGridClasses(row.cols, row.components?.length)}>
              {row.components?.map(componentName => renderComponent(componentName))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Default view config if none specified
const getDefaultViewConfig = () => ({
  header: {
    title: null, // Will use workflow.name
    actions: ['manualTrigger'],
  },
  layout: [
    { row: 1, components: ['statusCard'], cols: 'full' },
    { row: 2, components: ['scheduleControl', 'resultsSummary'], cols: 'half' },
    { row: 3, components: ['logsTable'], cols: 'full' },
  ],
});

export default WorkflowDashboardView;
